/**
 * MetricProgressScale — Barra de progreso con escala de hitos para tarjetas KPI.
 *
 * QUÉ HACE: pinta la barra de progreso (con etiqueta `%` opcional a la derecha) y
 *   debajo una escala de marcas verticales con su etiqueta alineada a la posición
 *   (0–100 %). La usa "Tareas Pendientes" para mostrar los hitos del día.
 * QUÉ INVOCA: MetricProgressBar (UI compartida).
 * QUÉ EXPORTA: MetricProgressScale, MetricProgressMilestone.
 * QUÉ NO DEBE HACER: no calcula métricas ni accede a datos.
 */
import { MetricProgressBar } from "./MetricProgressBar";

/** Hito de la escala. */
export interface MetricProgressMilestone {
  /** Posición en % sobre el ancho de la barra (0–100). */
  position: number;
  /** Etiqueta bajo la marca (p. ej. "4 (Tot)"). */
  label: string;
}

interface MetricProgressScaleProps {
  /** Porcentaje de avance 0–100. */
  value: number;
  /** Clases Tailwind del relleno de la barra. */
  fillClassName?: string;
  /** Clases Tailwind del alto del carril. */
  heightClassName?: string;
  /** Etiqueta a la derecha de la barra (p. ej. "25%"). */
  trailingLabel?: string;
  milestones: MetricProgressMilestone[];
  className?: string;
}

/** Alineación horizontal de la marca/etiqueta según su posición. */
const edgeAlign = (position: number): string => {
  if (position <= 0) return "translate-x-0";
  if (position >= 100) return "-translate-x-full";
  return "-translate-x-1/2";
};

export const MetricProgressScale = ({
  value,
  fillClassName = "bg-blue-500",
  heightClassName = "h-3",
  trailingLabel,
  milestones,
  className = "",
}: MetricProgressScaleProps) => {
  const clamp = (position: number): number =>
    Math.max(0, Math.min(100, position));

  return (
    <div className={className}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <MetricProgressBar
            value={value}
            fillClassName={fillClassName}
            heightClassName={heightClassName}
            className="w-full"
          />

          <div className="relative mt-1 h-1.5">
            {milestones.map((milestone) => (
              <span
                key={`tick-${milestone.position}-${milestone.label}`}
                className={`absolute top-0 h-1.5 w-px bg-slate-300 dark:bg-slate-600 ${edgeAlign(
                  clamp(milestone.position),
                )}`}
                style={{ left: `${clamp(milestone.position)}%` }}
              />
            ))}
          </div>

          <div className="relative mt-0.5 h-3">
            {milestones.map((milestone) => (
              <span
                key={`label-${milestone.position}-${milestone.label}`}
                className={`absolute top-0 whitespace-nowrap text-[6px] font-medium tabular-nums text-slate-500 sm:text-[7px] dark:text-slate-400 ${edgeAlign(
                  clamp(milestone.position),
                )}`}
                style={{ left: `${clamp(milestone.position)}%` }}
              >
                {milestone.label}
              </span>
            ))}
          </div>
        </div>

        {trailingLabel && (
          <span className="mt-0.5 shrink-0 text-[8px] font-semibold tabular-nums text-gray-500 sm:text-[9px] dark:text-slate-400">
            {trailingLabel}
          </span>
        )}
      </div>
    </div>
  );
};
